import { Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DryOfferRequestModel } from "@modules/+new/payments/features/fiat-deposit/domain/entities/dry-offer/dry-offer-request.model";
import {
	DryOffer,
	DryOfferAdapter
} from "@modules/+new/payments/features/fiat-deposit/domain/entities/dry-offer/dry-offer.model";
import { BasicApiObject, Helper, parseApiResponseArray, parseApiResponseObject } from "@helpers/functions";
import { DryOfferDto } from "@modules/+new/payments/features/fiat-deposit/infrastructure/dto/dry-offer.dto";
import { Bank, BankAdapter } from "@modules/+new/payments/features/fiat-deposit/domain/entities/bank/bank.model";
import { BankDto } from "@modules/+new/payments/features/fiat-deposit/infrastructure/dto/bank.dto";
import { BankNestLevel } from "@modules/+new/payments/features/fiat-deposit/modules/bank-selector/domain/entities/bank-nest-level.enum";
import { GetBankTransferInformationRequest } from "@modules/+new/payments/features/fiat-deposit/domain/entities/manual-deposit/get-deposit-information-request.model";
import {
	BankTransferInformation,
	BankTransferInformationAdapter
} from "@modules/+new/payments/features/fiat-deposit/domain/entities/manual-deposit/bank-transfer-information.model";
import { BankTransferInformationDto } from "@modules/+new/payments/features/fiat-deposit/infrastructure/dto/bank-transfer-information.dto";
import { FiatDepositConfig } from "@modules/+new/payments/features/fiat-deposit/domain/config/fiat-deposit-config";

@Injectable({
	providedIn: "any"
})
export class FiatDepositRemote {
	private dryOfferAdapter = new DryOfferAdapter();
	public constructor(private apiService: ApicallService) {}

	public getDryOffer$(walletId: string, data: DryOfferRequestModel): Observable<DryOffer> {
		const option = new ApicallOption(null, data);
		option.apiVersion = "v2";

		return this.apiService.post(`fiatwallet/${walletId}/deposit/dry-offer`, option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((offer: BasicApiObject) => parseApiResponseObject<DryOfferDto>(offer)),
			map((offer: DryOfferDto) => this.dryOfferAdapter.adapt(offer))
		);
	}

	public getBank$(bankProviderName: string, bankId: string): Observable<Bank> {
		const option = new ApicallOption();
		option.apiVersion = "v1";

		return this.apiService.get(`bank-selector/${bankProviderName}/banks/${bankId}`, option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((apiObject: BasicApiObject) => parseApiResponseObject<BankDto>(apiObject)),
			map((dto: BankDto) => new BankAdapter().adapt(dto))
		);
	}

	public getBanks$(
		bankProviderName: string,
		query: string = "",
		page: number = FiatDepositConfig.defaultPageToLoad,
		page_size: number = FiatDepositConfig.defaultPageSize
	): Observable<Bank[]> {
		const option = new ApicallOption();
		option.params = { query, level: BankNestLevel.First, page, page_size };
		return this.apiService.get(`bank-selector/${bankProviderName}/banks`, option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((banks: BasicApiObject[]) => parseApiResponseArray<BankDto>(banks)),
			map((banks: BankDto[]) => new BankAdapter().adaptMany(banks))
		);
	}

	public getBankChildList$(
		bankProviderName: string,
		parentBankId: string,
		query: string = "",
		page: number = FiatDepositConfig.defaultPageToLoad,
		page_size: number = FiatDepositConfig.defaultPageSize
	): Observable<Bank[]> {
		const option = new ApicallOption();
		option.params = { query, level: BankNestLevel.Last, page, page_size };
		return this.apiService.get(`bank-selector/${bankProviderName}/banks/${parentBankId}/child-list`, option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((childList: BasicApiObject[]) => parseApiResponseArray<BankDto>(childList)),
			map((childList: BankDto[]) => new BankAdapter().adaptMany(childList))
		);
	}

	public getBankTransferInformation$(
		userId: string,
		request: GetBankTransferInformationRequest
	): Observable<BankTransferInformation> {
		const { fiatId, paymentOptionId, amount, sendEmail } = request;
		const url = `users/${userId}/payment-options/${paymentOptionId}/fiats/${fiatId}/information`;
		const option = new ApicallOption();
		option.params = { amount, email: sendEmail ? 1 : null };

		return this.apiService.get(url, option).pipe(
			catchError((error: unknown) => Helper.handleError$(error)),
			map((child: BasicApiObject) => parseApiResponseObject<BankTransferInformationDto>(child)),
			map((child: BankTransferInformationDto) => new BankTransferInformationAdapter().adapt(child))
		);
	}

	public getLastUsedBanks$(bankProviderName: string): Observable<Bank[]> {
		return this.apiService.get(`bank-selector/${bankProviderName}/banks/recently_used`).pipe(
			map((apiObject: BasicApiObject[]) => parseApiResponseArray<BankDto>(apiObject)),
			map((dto: BankDto[]) => new BankAdapter().adaptMany(dto))
		);
	}
}
